'use client';

import { FC, useMemo, useState } from 'react';
import { Button, Typography } from '@mui/material';

import {
  StyledButton,
  RowBox,
  OfferTypeRow,
  StyledSubmitButton,
  ButtonsBox,
  FilterBox,
  FiltersContainer,
  InputsBox,
  MobileFilterButton,
  MobileSearchRow,
  DesktopSearchField,
} from './styled';

import { OfferType, ReferenceLocation, SearchTabs } from '@/types';
import {
  Range,
  CheckboxGroupMenu,
  WalletIcon,
  HouseKeyIcon,
  SearchIcon,
  MapIcon,
  FiltersIcon,
  LocationAutocomplete,
} from '@/components';
import { CATEGORY_ORDER, COLORS, MAX_FILTERS_RANGE_VALUES } from '@/constants';

import { getClampToMax } from '@/common/helpers';
import { buildGoToMapPatch } from '@/features/pages/housing-list/utils/collectAdmunitIds';
import { useFilters, useHousingInitialFilters } from '@/hooks';
import { PATHS } from '@/constants';
import { useGetHeroFilters } from '@/queris';
import { useTranslations } from 'next-intl';
import { useRouter } from '@/config/i18n/navigation';
import dynamic from 'next/dynamic';

const HeroMobileFiltersModal = dynamic(() =>
  import('./HeroMobileFiltersModal').then((m) => ({ default: m.HeroMobileFiltersModal })),
);

type SearchFilterProps = {
  fullWidth?: boolean;
};

export const SearchFilter: FC<SearchFilterProps> = ({ fullWidth = false }) => {
  const t = useTranslations();
  const router = useRouter();

  const { filters, setFilters, getQueryString, resetFilters } = useFilters({
    options: { syncUrl: false },
  });

  const { data, isPending } = useGetHeroFilters();

  const { categoriesMenuList, rangesDimensions, offerTypeList } = useHousingInitialFilters(
    data ?? null,
  );

  const [locations, setLocations] = useState<ReferenceLocation[]>(filters.locations ?? []);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const sellMaxValue = useMemo(
    () => getClampToMax(rangesDimensions.price[1], MAX_FILTERS_RANGE_VALUES.SELL_PRICE),
    [rangesDimensions.price],
  );

  const rentMaxValue = useMemo(
    () => getClampToMax(rangesDimensions.rentPrice[1], MAX_FILTERS_RANGE_VALUES.RENT_PRICE),
    [rangesDimensions.rentPrice],
  );

  const handleFilterTypeChange = (n: OfferType) => {
    setFilters({ offerTypeValue: n });
  };

  const handleSearch = (isMap = false) => {
    const overrides = isMap
      ? buildGoToMapPatch({ locations })
      : { locations, tabs: SearchTabs.LIST };
    const query = getQueryString(overrides);
    const queryParams = query.split('?')[1] || '';
    router.push(`/${PATHS.HOUSING}${queryParams ? '?' + queryParams : ''}`);
  };

  return (
    <FiltersContainer $fullWidth={fullWidth}>
      <FilterBox>
        <OfferTypeRow>
          <StyledButton
            variant="outlined"
            $isActive={filters.offerTypeValue === OfferType.SELL}
            onClick={() => handleFilterTypeChange(OfferType.SELL)}
            startIcon={
              <WalletIcon
                fill={filters.offerTypeValue === OfferType.SELL ? COLORS.orange : COLORS.gray}
              />
            }
          >
            <Typography textTransform={'none'} fontSize={18} lineHeight={1} fontWeight={600}>
              {t('mainHero.filter.buy')}
            </Typography>
          </StyledButton>

          <StyledButton
            variant="outlined"
            $isActive={filters.offerTypeValue === OfferType.RENT}
            onClick={() => handleFilterTypeChange(OfferType.RENT)}
            startIcon={
              <HouseKeyIcon
                fill={filters.offerTypeValue === OfferType.RENT ? COLORS.orange : COLORS.gray}
              />
            }
          >
            <Typography textTransform={'none'} fontSize={18} lineHeight={1} fontWeight={600}>
              {t('mainHero.filter.rent')}
            </Typography>
          </StyledButton>
        </OfferTypeRow>

        <RowBox $gap={16} $mobileColumn>
          <InputsBox>
            <CheckboxGroupMenu
              label={t('searchResult.group')}
              key={t('searchResult.group')}
              items={categoriesMenuList}
              value={filters.categoriesValue}
              isSelect
              hideCount
              onChange={(value) => setFilters({ categoriesValue: value })}
              order={CATEGORY_ORDER}
            />

            <Range
              label={t('searchResult.rooms')}
              minValue={rangesDimensions.roomNumber[0] || 0}
              maxValue={getClampToMax(
                rangesDimensions.roomNumber[1],
                MAX_FILTERS_RANGE_VALUES.ROOMS,
              )}
              value={filters.roomNumberValue}
              setValue={(value) => setFilters({ roomNumberValue: value })}
              step={1}
              isSelect
              isAccordion={false}
            />

            {filters.offerTypeValue === OfferType.SELL ? (
              <Range
                key="sell-price-range"
                isPrice
                label={t('searchResult.purchasePrice')}
                minValue={rangesDimensions.price[0] || 0}
                maxValue={sellMaxValue}
                value={filters.priceValue}
                setValue={(value) => setFilters({ priceValue: value })}
                step={1}
                isSelect
                isAccordion={false}
                width="230px"
              />
            ) : (
              <Range
                key="rent-price-range"
                isPrice
                label={t('searchResult.purchasePrice')}
                minValue={rangesDimensions.rentPrice[0] || 0}
                maxValue={rentMaxValue}
                value={filters.rentPriceValue}
                setValue={(value) => setFilters({ rentPriceValue: value })}
                step={1}
                isSelect
                isAccordion={false}
                width="230px"
              />
            )}
          </InputsBox>

          <DesktopSearchField>
            <LocationAutocomplete
              value={locations}
              onChange={setLocations}
              label={t('mainHero.filter.searchPlaceholder')}
              placeholder="z.B. Salzburg, 5020"
            />
          </DesktopSearchField>

          <MobileSearchRow>
            <LocationAutocomplete
              value={locations}
              onChange={setLocations}
              label={t('mainHero.filter.searchPlaceholder')}
              placeholder="z.B. Salzburg, 5020"
            />
            <MobileFilterButton variant="outlined" onClick={() => setMobileFiltersOpen(true)}>
              <FiltersIcon />
            </MobileFilterButton>
          </MobileSearchRow>
        </RowBox>
      </FilterBox>

      <ButtonsBox>
        <StyledSubmitButton
          onClick={() => handleSearch(true)}
          disabled={isPending}
          $color={'primary/900'}
          $bgColor="white"
          variant="outlined"
          startIcon={<MapIcon />}
        >
          <Typography fontSize={16} fontWeight={600} textTransform={'none'} lineHeight={1}>
            {t('mainHero.filter.button.map')}
          </Typography>
        </StyledSubmitButton>

        <Button
          onClick={() => handleSearch()}
          variant="secondary"
          startIcon={<SearchIcon />}
          disabled={isPending}
        >
          <Typography fontSize={16} fontWeight={600} textTransform={'none'} lineHeight={1}>
            {`${t('mainHero.filter.button.submit')}`}
          </Typography>
        </Button>
      </ButtonsBox>

      <HeroMobileFiltersModal
        isOpen={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        onSearch={() => {
          setMobileFiltersOpen(false);
          handleSearch();
        }}
        onSearchMap={() => {
          setMobileFiltersOpen(false);
          handleSearch(true);
        }}
        offerTypeList={offerTypeList}
        offerTypeValue={filters.offerTypeValue}
        setOfferTypeValue={(values) => {
          if (values.length === 0) return;
          setFilters({ offerTypeValue: values[values.length - 1].code });
        }}
        categoriesMenuList={categoriesMenuList}
        categoriesValue={filters.categoriesValue}
        setCategoriesValue={(value) => setFilters({ categoriesValue: value })}
        rangesDimensions={rangesDimensions}
        roomNumberValue={filters.roomNumberValue}
        setRoomNumberValue={(value) => setFilters({ roomNumberValue: value })}
        priceValue={filters.priceValue}
        setPriceValue={(value) => setFilters({ priceValue: value })}
        rentPriceValue={filters.rentPriceValue}
        setRentPriceValue={(value) => setFilters({ rentPriceValue: value })}
        onReset={() => resetFilters()}
        isPending={isPending}
        isFiltersLoading={isPending}
      />
    </FiltersContainer>
  );
};
